<template>
    <FilterBase :title="title">
        <InputRangeTwoKnobbedWithInputs
            v-model="model"
            :min="extractRangeInputValue(bounds, 'min')"
            :max="extractRangeInputValue(bounds, 'max')"
            :step
            :value-formatter
            :debounce="500"
            :disabled
        />
    </FilterBase>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type {
    NonSkuCaratWeightRangeFragment,
    NonSkuCaratWeightRangeInput,
} from '@/graphql/api'
import { isUndefinedOrNull } from '@/helpers'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import InputRangeTwoKnobbedWithInputs from '@/components/ui/inputs/range/InputRangeTwoKnobbedWithInputs.vue'
import type {
    InputRangeModel,
    InputRangeTwoKnobbedValueFormatter,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

interface Props {
    modelValue: NonSkuCaratWeightRangeInput | null | undefined
    bounds: NonSkuCaratWeightRangeFragment
    step?: number
    title?: string
    valueFormatter?: InputRangeTwoKnobbedValueFormatter
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    step: 1,
    valueFormatter: undefined,
    title: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [v: NonSkuCaratWeightRangeInput]
}>()

const model = computed({
    get: (): InputRangeModel => [
        getValueOrBound('min'),
        getValueOrBound('max'),
    ],
    set: (v: InputRangeModel) => {
        const min = v[0]
        const max = v[1]

        emit('update:modelValue', {
            min: typeof min === 'number' ? String(min) : undefined,
            max: typeof max === 'number' ? String(max) : undefined,
        })
    },
})

function getValueOrBound(key: keyof RangeInput): number | undefined {
    return extractRangeInputValue(props.modelValue || props.bounds, key)
}

type RangeInput = Pick<NonSkuCaratWeightRangeInput, 'min' | 'max'>

function extractRangeInputValue(
    value: RangeInput,
    key: keyof RangeInput
): number | undefined {
    const val = value[key]

    if (isUndefinedOrNull(val)) {
        return undefined
    }

    const asNumber = Number(val)

    if (isNaN(asNumber)) {
        return undefined
    }

    return asNumber
}
</script>
